import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../App";
import { Button } from "../components/ui/button";
import { ArrowLeft, Shield } from "lucide-react";

const SectionHeading = ({ num, title }) => (
  <h2 className="text-xl font-medium text-sky-800 mb-3">{num}. {title}</h2>
);

const SubHeading = ({ num, title }) => (
  <h3 className="text-lg font-medium text-sky-700 mt-4 mb-2">{num} {title}</h3>
);

const PrivacyPolicy = () => {
  const { t } = useTranslation();
  const authCtx = useContext(AuthContext);
  const user = authCtx?.user;
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-sky-50" data-testid="privacy-policy-page">
      <div className="fixed top-0 left-0 w-96 h-96 bg-indigo-300/15 rounded-full blur-3xl pointer-events-none" />

      <header className="fixed top-0 w-full z-50 glass-header">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate(user ? "/dashboard" : "/")}
              className="text-sky-600 hover:text-sky-800"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-indigo-500" strokeWidth={1.5} />
              <span className="text-sm text-sky-700">Legal</span>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-16 px-4 md:px-8">
        <div className="max-w-4xl mx-auto space-y-8">

          {/* ===== PART I: TERMS OF SERVICE ===== */}
          <div className="glass-card p-8 md:p-12">
            <h1 className="text-3xl font-light text-sky-900 mb-2" style={{ fontFamily: "Outfit" }}>
              Terms of Service
            </h1>
            <p className="text-sm text-slate-400 mb-8">Last updated: April 7, 2026 &middot; LumiFit, Pf&auml;ffikon ZH, Switzerland</p>

            <div className="prose prose-slate max-w-none space-y-8 text-slate-700 leading-relaxed">

              <section>
                <SectionHeading num="1" title="Acceptance of Terms" />
                <p>
                  By accessing, downloading, or using the LumiFit application (&quot;App&quot;), you agree to be bound by these Terms of Service. If you do not agree to all the terms and conditions, you must immediately cease using the App. This agreement constitutes a legally binding contract between you and LumiFit, headquartered in Pf&auml;ffikon ZH, Switzerland.
                </p>
              </section>

              <section>
                <SectionHeading num="2" title="No Medical Advice; Not a Healthcare Provider" />
                <p>
                  LumiFit and its affiliated services, content, and features do not provide medical advice, diagnosis, or treatment. The App is intended strictly for informational, educational, and general wellness purposes. You acknowledge and agree that LumiFit is not a healthcare provider, and the App is not a substitute for professional medical consultation, advice, or treatment from a qualified physician. Never disregard professional medical advice or delay in seeking it because of something you have read, seen, or heard on LumiFit. Always consult with a physician before beginning any new fitness or wellness regimen.
                </p>
              </section>

              <section>
                <SectionHeading num="3" title="Explicit Assumption of Risk and User Responsibility" />
                <p>
                  You expressly acknowledge that participation in fitness, wellness, and dietary programs involves inherent risks, including but not limited to the risk of physical or psychological injury, pain, suffering, illness, or death. You voluntarily assume all known and unknown risks associated with your use of LumiFit. You agree that you are exclusively responsible for your own health, safety, and physical well-being. Any routines, guidelines, or advice provided by LumiFit are followed entirely at your own risk.
                </p>
              </section>

              <section>
                <SectionHeading num="4" title="Account Registration & Security" />
                <p>To access specific features, including the body and mind evaluation questionnaires and AI-generated workout plans, you must register an account.</p>
                <ul className="list-disc pl-6 space-y-1 mt-2">
                  <li><strong>Age Requirement:</strong> You must be at least <strong>18 years of age</strong> to create an account. By registering, you represent and warrant that you meet this age requirement.</li>
                  <li><strong>Accuracy:</strong> You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate.</li>
                  <li><strong>Security:</strong> You are solely responsible for maintaining the confidentiality of your account credentials. You agree to notify LumiFit immediately of any unauthorized use of your account. LumiFit is not liable for any loss or damage arising from your failure to protect your login information.</li>
                </ul>
              </section>

              <section>
                <SectionHeading num="5" title="User Conduct & Prohibited Activities" />
                <p>You agree to use LumiFit solely for your personal, non-commercial fitness and wellness purposes. You strictly agree not to:</p>
                <ul className="list-disc pl-6 space-y-1 mt-2">
                  <li>Input false, misleading, or malicious data into the AI-evaluated questionnaires.</li>
                  <li>Reverse engineer, decompile, disassemble, or attempt to derive the source code of the App, including its AI algorithms, evaluation parameters, and mindfulness content.</li>
                  <li>Scrape, extract, or harvest any data or content from the App using automated means.</li>
                  <li>Share, distribute, or resell LumiFit&apos;s generated workout plans, mindfulness exercises, or evaluation results.</li>
                  <li>Use the App in any manner that could disable, overburden, or impair its functionality.</li>
                </ul>
              </section>

              <section>
                <SectionHeading num="6" title="Intellectual Property Rights" />
                <p>
                  All content, features, and functionality available through the App — including but not limited to AI-generated workout routines, mindfulness audio and text, evaluation questionnaires, UI/UX design, text, graphics, logos, and software — are the exclusive property of LumiFit and are protected by Swiss and international copyright, trademark, and intellectual property laws. You are granted a limited, non-exclusive, non-transferable, and revocable license to use the App for personal use. No ownership rights are transferred to you under these Terms.
                </p>
              </section>

              <section>
                <SectionHeading num="7" title="Limitation of Liability and Indemnification" />
                <p>
                  To the maximum extent permitted by applicable law, in no event shall LumiFit, its founders, affiliates, employees, or licensors be liable for any direct, indirect, punitive, incidental, special, consequential, or exemplary damages, including without limitation damages for personal injury, health complications, or death, arising out of or in any way connected with the use or performance of the App. You agree to indemnify, defend, and hold harmless LumiFit from any claims, losses, liability, or expenses arising from your use of the service or your violation of these Terms.
                </p>
              </section>

              <section>
                <SectionHeading num="8" title="Mandatory Binding Arbitration and Class Action Waiver" />
                <p>
                  Any dispute, claim, or controversy arising out of or relating to these Terms or the breach, termination, enforcement, interpretation, or validity thereof, shall be determined by binding individual arbitration rather than in a court of law. You and LumiFit expressly waive the right to a trial by jury or to participate in any class action, mass arbitration, or representative proceeding.
                </p>
              </section>

              <section>
                <SectionHeading num="9" title="Termination" />
                <p>
                  LumiFit reserves the right, in its sole discretion and without prior notice or liability, to suspend, disable, or terminate your account and access to the App at any time, for any reason, including but not limited to a breach of these Terms. Upon termination, your right to use the App will immediately cease. All provisions of the Terms which by their nature should survive termination, including ownership provisions, warranty disclaimers, indemnity, and limitations of liability, shall survive.
                </p>
              </section>

              <section>
                <SectionHeading num="10" title="Governing Law & Venue" />
                <p>
                  These Terms of Service, and any separate agreements whereby we provide you services, shall be governed by and construed in accordance with the substantive laws of Switzerland, excluding its conflicts of law principles and the United Nations Convention on Contracts for the International Sale of Goods. Any dispute, controversy, or claim arising out of, or in relation to, this agreement, including the validity, invalidity, breach, or termination thereof, shall be subject to the exclusive jurisdiction of the competent courts of the Canton of Zurich, Switzerland.
                </p>
              </section>

            </div>
          </div>

          {/* ===== PART II: PRIVACY POLICY ===== */}
          <div className="glass-card p-8 md:p-12">
            <h1 className="text-3xl font-light text-sky-900 mb-2" style={{ fontFamily: "Outfit" }}>
              Privacy Policy
            </h1>
            <p className="text-sm text-slate-400 mb-8">Last updated: April 7, 2026</p>

            <div className="prose prose-slate max-w-none space-y-8 text-slate-700 leading-relaxed">

              <section>
                <SectionHeading num="1" title="Introduction & Scope" />
                <p>
                  LumiFit (&quot;we&quot;, &quot;us&quot;, &quot;the Service&quot;) is an AI-powered wellness platform that combines physical fitness training with cognitive strength and emotional resilience exercises. This Privacy Policy explains how we collect, process, store, and protect your personal data — including sensitive health and psychological data — in compliance with the EU General Data Protection Regulation (GDPR), the Swiss Federal Act on Data Protection (nFADSP/DSG), and other applicable data protection laws.
                </p>
                <p>
                  By creating an account and using LumiFit, you acknowledge that you have read, understood, and agree to the practices described in this policy. If you do not agree, please do not use the Service.
                </p>
              </section>

              <section>
                <SectionHeading num="2" title="Data We Collect" />
                <p>We collect the following categories of personal data:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Account Data:</strong> Email address, display name, authentication tokens, and language preference.</li>
                  <li><strong>Psychological Assessment Data:</strong> Your responses to daily wellness questionnaires and clinical-grade instruments (PHQ-9, GAD-7, PSS-10, WHO-5, PSQI), including computed severity scores.</li>
                  <li><strong>Fitness & Cognitive Data:</strong> Exercise completion records, cognitive game scores, mindfulness session logs, streak data, and AI-generated training plans.</li>
                  <li><strong>Usage Data:</strong> Feature interaction logs, session timestamps, and device information used solely for Service improvement.</li>
                </ul>
                <p className="mt-3">
                  <strong>We do not collect:</strong> Location data, biometric data, financial data beyond what Stripe processes independently for subscriptions, or any data from minors under 18.
                </p>
              </section>

              <section>
                <SectionHeading num="3" title="AI-Powered Data Processing & Automated Decision-Making" />
                <p>
                  LumiFit uses artificial intelligence to evaluate your questionnaire responses and generate personalized wellness plans. This section explains exactly how this automated processing works, in accordance with GDPR Article 22 (automated individual decision-making).
                </p>

                <SubHeading num="3.1" title="How the AI Evaluates Your Data" />
                <p>
                  When you complete a daily questionnaire or clinical assessment, your anonymized responses are transmitted to our AI processing pipeline. The AI analyzes your response patterns against established clinical scoring frameworks (e.g., PHQ-9 scoring for depression severity, GAD-7 for anxiety levels) and cross-references them with your historical wellness trajectory. Based on this evaluation, the AI generates a personalized training plan that balances physical exercises with cognitive and emotional resilience activities tailored to your current state.
                </p>
                <p>
                  <strong>Important:</strong> The AI does not make medical diagnoses. All severity classifications (e.g., &quot;mild&quot;, &quot;moderate&quot;, &quot;severe&quot;) are derived from standardized clinical scoring instruments and are presented for informational and wellness purposes only. LumiFit is not a substitute for professional medical advice, diagnosis, or treatment.
                </p>

                <SubHeading num="3.2" title="Third-Party AI Processors" />
                <p>
                  Our AI-powered evaluation is performed using the OpenAI API (operated by OpenAI, L.L.C., San Francisco, CA, USA) as a data sub-processor. When your questionnaire data is sent for AI evaluation:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Only the minimum necessary data is transmitted (your anonymized responses and relevant context — never your email, name, or account identifiers).</li>
                  <li>Data is transmitted over encrypted channels (TLS 1.3).</li>
                  <li>OpenAI operates under a <strong>Zero Data Retention (ZDR)</strong> policy for API requests: your data is not stored, logged, or used to train OpenAI&apos;s models. This is contractually guaranteed under our Data Processing Agreement (DPA) with OpenAI.</li>
                  <li>No other third-party AI provider receives your health data.</li>
                </ul>

                <SubHeading num="3.3" title="Your Right to Contest Automated Decisions" />
                <p>
                  Under GDPR Article 22, you have the right to: (a) obtain meaningful information about the logic involved in AI-generated assessments; (b) express your point of view and contest any AI-generated severity assessment; and (c) request human review of any automated decision. To exercise these rights, contact us at privacy@lumifit.me.
                </p>
              </section>

              <section>
                <SectionHeading num="4" title="Data Storage, Localization & Security" />

                <SubHeading num="4.1" title="Data Localization" />
                <p>
                  Your personal data is stored on servers located within the European Union (EU/EEA). Our primary database infrastructure is hosted via MongoDB Atlas on cloud infrastructure within EU data centers. For AI processing via the OpenAI API, data may be transiently processed on servers in the United States; however, no persistent storage occurs outside the EU due to our Zero Data Retention agreement, and appropriate Standard Contractual Clauses (SCCs) and Transfer Impact Assessments (TIAs) are in place in accordance with GDPR Chapter V.
                </p>

                <SubHeading num="4.2" title="Encryption & Security Measures" />
                <p>Given the sensitive nature of psychological and health-related data, we implement multiple layers of protection:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>In Transit:</strong> All data is encrypted using TLS 1.3 between your device, our servers, and any sub-processors.</li>
                  <li><strong>At Rest:</strong> All database records containing health data are encrypted using AES-256 encryption at the storage layer.</li>
                  <li><strong>Authentication:</strong> Passwords are hashed using bcrypt with per-user salt. Session tokens are cryptographically signed JWTs with short expiration windows.</li>
                  <li><strong>Access Control:</strong> Strict role-based access. No LumiFit employee can access individual user health data without a documented, auditable reason.</li>
                </ul>
              </section>

              <section>
                <SectionHeading num="5" title="Data Retention & Deletion Protocol" />

                <SubHeading num="5.1" title="Retention Schedule" />
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Daily Questionnaire Responses:</strong> Retained for 12 months from submission date, then automatically purged.</li>
                  <li><strong>Clinical Assessment Data (Deep Dive):</strong> Retained for 6 months, then automatically purged. Aggregate severity scores (without individual answers) may be retained for up to 12 months for trend visualization.</li>
                  <li><strong>AI-Generated Training Plans:</strong> Retained for 30 days. Historical plans are purged automatically as they serve no further purpose once superseded.</li>
                  <li><strong>Cognitive Game Scores & Fitness Logs:</strong> Retained for the lifetime of the account (non-sensitive performance data).</li>
                  <li><strong>Account Data:</strong> Retained until account deletion is requested.</li>
                </ul>

                <SubHeading num="5.2" title="Account Deletion & Right to be Forgotten" />
                <p>
                  You may request complete account deletion at any time through Settings or by contacting privacy@lumifit.me. Upon receiving a deletion request, we execute the following protocol within <strong>30 calendar days</strong>:
                </p>
                <ol className="list-decimal pl-6 space-y-1">
                  <li>All personal identifiers (email, name, authentication tokens) are permanently deleted from our primary database.</li>
                  <li>All questionnaire responses, clinical assessment data, AI-generated plans, and psychological severity scores are permanently and irreversibly purged.</li>
                  <li>All game scores, exercise logs, streak data, and mindfulness records are permanently deleted.</li>
                  <li>Stripe subscription data is handled per Stripe&apos;s own retention policy; we instruct Stripe to delete associated customer records.</li>
                  <li>Backup systems purge the corresponding data within an additional 30 days (60 days total maximum).</li>
                </ol>
                <p className="mt-2">
                  This deletion is <strong>permanent and irreversible</strong>. We issue a confirmation email upon completion.
                </p>
              </section>

              <section>
                <SectionHeading num="6" title="Data Minimization & Purpose Limitation" />
                <p>We adhere to the principle of data minimization. We collect only the data strictly necessary to provide the Service. Specifically:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Questionnaire data is collected solely to generate your personalized wellness plan and track your progress. It is not used for any other purpose.</li>
                  <li>When data is sent to the AI for evaluation, only anonymized question-answer pairs and relevant context are transmitted — never your identity.</li>
                  <li>We do not sell, rent, or share your personal data with advertisers, data brokers, or any third parties not explicitly listed in this policy.</li>
                </ul>
              </section>

              <section>
                <SectionHeading num="7" title="Anonymization & AI Model Training" />
                <p>
                  <strong>LumiFit does not use your personal health data to train any AI models.</strong> Your questionnaire responses and wellness data are never fed into any training pipeline, whether internal or external.
                </p>
                <p>
                  If, in the future, we conduct research to improve our wellness algorithms using aggregated user data, the following irreversible anonymization protocol will be applied before any data enters a training pipeline:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>All personal identifiers are stripped (email, user ID, name, IP address).</li>
                  <li>Temporal markers are generalized (exact dates replaced with relative timeframes).</li>
                  <li>Response sets are aggregated into statistical cohorts of minimum 50 users, making re-identification statistically impossible.</li>
                  <li>k-anonymity (k &ge; 50) is enforced on all anonymized datasets.</li>
                </ul>
                <p className="mt-2">
                  Any such research use will require a separate, explicit opt-in consent and will be communicated through an updated version of this policy.
                </p>
              </section>

              <section>
                <SectionHeading num="8" title="Sub-Processors & Third Parties" />
                <p>The following third-party sub-processors may process your data in the course of providing the Service:</p>
                <div className="overflow-x-auto mt-3">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-2 pr-4 font-medium text-sky-800">Sub-Processor</th>
                        <th className="text-left py-2 pr-4 font-medium text-sky-800">Purpose</th>
                        <th className="text-left py-2 pr-4 font-medium text-sky-800">Data Shared</th>
                        <th className="text-left py-2 font-medium text-sky-800">Location</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-600">
                      <tr className="border-b border-slate-100">
                        <td className="py-2 pr-4">OpenAI, L.L.C.</td>
                        <td className="py-2 pr-4">AI evaluation of questionnaires</td>
                        <td className="py-2 pr-4">Anonymized responses only</td>
                        <td className="py-2">USA (ZDR, SCCs)</td>
                      </tr>
                      <tr className="border-b border-slate-100">
                        <td className="py-2 pr-4">Stripe, Inc.</td>
                        <td className="py-2 pr-4">Payment processing</td>
                        <td className="py-2 pr-4">Email, subscription status</td>
                        <td className="py-2">USA (SCCs)</td>
                      </tr>
                      <tr className="border-b border-slate-100">
                        <td className="py-2 pr-4">MongoDB, Inc.</td>
                        <td className="py-2 pr-4">Database hosting (Atlas)</td>
                        <td className="py-2 pr-4">All user data (encrypted)</td>
                        <td className="py-2">EU (Frankfurt)</td>
                      </tr>
                      <tr>
                        <td className="py-2 pr-4">Google (OAuth)</td>
                        <td className="py-2 pr-4">Authentication</td>
                        <td className="py-2 pr-4">Email, name (from Google)</td>
                        <td className="py-2">USA (SCCs)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="mt-3">
                  Data Processing Agreements (DPAs) are in place with all sub-processors. We conduct annual reviews of all sub-processor compliance.
                </p>
              </section>

              <section>
                <SectionHeading num="9" title="Your Rights Under GDPR" />
                <p>As a data subject, you have the following rights:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Right of Access (Art. 15):</strong> Request a complete copy of all personal data we hold about you.</li>
                  <li><strong>Right to Rectification (Art. 16):</strong> Correct inaccurate personal data.</li>
                  <li><strong>Right to Erasure (Art. 17):</strong> Request permanent deletion of all your data (&quot;Right to be Forgotten&quot;).</li>
                  <li><strong>Right to Restriction (Art. 18):</strong> Restrict processing of your data while a dispute is resolved.</li>
                  <li><strong>Right to Data Portability (Art. 20):</strong> Receive your data in a structured, machine-readable format (JSON).</li>
                  <li><strong>Right to Object (Art. 21):</strong> Object to processing of your data, including AI-based profiling.</li>
                  <li><strong>Right to Contest Automated Decisions (Art. 22):</strong> Request human review of any AI-generated assessment.</li>
                </ul>
                <p className="mt-2">
                  To exercise any of these rights, contact us at <strong>privacy@lumifit.me</strong>. We will respond within 30 days.
                </p>
              </section>

              <section>
                <SectionHeading num="10" title="Legal Basis for Processing" />
                <p>We process your data on the following legal bases under GDPR Article 6:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Consent (Art. 6(1)(a)):</strong> You provide explicit consent when completing questionnaires and clinical assessments. This consent is freely given, specific, informed, and unambiguous. You may withdraw consent at any time.</li>
                  <li><strong>Contract Performance (Art. 6(1)(b)):</strong> Processing necessary to provide the LumiFit service you have subscribed to.</li>
                  <li><strong>Legitimate Interest (Art. 6(1)(f)):</strong> Aggregated, anonymized analytics to improve Service quality — balanced against your rights via a Legitimate Interest Assessment (LIA).</li>
                </ul>
                <p className="mt-2">
                  For special categories of data (health data) under Article 9, processing is based exclusively on your <strong>explicit consent</strong> (Art. 9(2)(a)), which you grant during account setup and may revoke at any time.
                </p>
              </section>

              <section>
                <SectionHeading num="11" title="Contact & Supervisory Authority" />
                <p>
                  <strong>Data Controller:</strong> LumiFit, Pf&auml;ffikon ZH, Switzerland<br />
                  <strong>Data Protection Contact:</strong> privacy@lumifit.me
                </p>
                <p className="mt-2">
                  If you believe your data protection rights have been violated, you have the right to lodge a complaint with your local supervisory authority. For users in Switzerland, this is the Federal Data Protection and Information Commissioner (FDPIC). For users in the EU, contact your national data protection authority.
                </p>
              </section>

              <section>
                <SectionHeading num="12" title="Changes to This Policy" />
                <p>
                  We will notify you of any material changes to this policy at least 30 days before they take effect, via email and an in-app notification. Continued use of the Service after the effective date constitutes acceptance of the updated policy. If the changes affect the processing of your health data, we will seek fresh explicit consent.
                </p>
              </section>

            </div>
          </div>

        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
